/**
 * FinancialDashboard.jsx
 *
 * Landing page for the `financial`/`financial-super-sec` roles — pools BACS
 * references still needing reconciliation and refund requests still `needed`
 * across every event the secretary has access to, so they don't have to
 * click through each event individually to find out what needs attention
 * today. The classic per-event FinancialEntryList (detail view, CSV export)
 * stays reachable via "Browse all events →".
 *
 * Deliberately reuses existing per-event endpoints (financial-view,
 * refund-requests, bacs-actual-amount) rather than adding new aggregate
 * backend routes — a financial secretary's event list is a season
 * (~15-20 events), so fetching them in parallel on load is trivial.
 *
 * Covers the current year AND any later season already on the calendar -
 * online entries (and so BACS payments) for next season's events start
 * arriving before 1 January, and this page is for "what needs attention",
 * so it mustn't hide those behind a year filter. Earlier seasons are
 * reachable via the event list's own year selector.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  api, API_KEY, fmtCurrency, fmtAdjustment, fmtDate, fmtDateTime,
  InlineEditCell, Spinner, EmptyState, refundStatusLabel, computeBacsGroups,
  RefundRequestForm, CURRENT_YEAR, useEventYears,
} from './SecretaryPortal';
import RefundActionDialog from './RefundActionDialog';

const eventLabel = (ev) => ev.eventname || `${ev.Hill} – ${fmtDate(ev.Date)}`;
const bacsRowKey = (row) => `${row.calendar_id}::${row.bac_reference}`;
// Only "not yet confirmed" belongs here - a confirmed BACS amount that
// doesn't match fees_total is a deliberate adjustment (partial refund
// processed elsewhere, negotiated fee, etc.), not something needing
// action. Confirmed 2026-08-24: a mismatch check here kept two legitimately
// verified Curborough BACS rows permanently stuck on the dashboard.
// The per-event view (EntryList's ⚠ mismatch warning) still surfaces
// stated-vs-actual discrepancies for anyone reconciling a single event.
const bacsNeedsAttention = (row) => row.bacs_actual_amount == null;

export default function FinancialDashboard({ sessionToken, onSelectEvent, onBrowseEvents }) {
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [bacsRows, setBacsRows]   = useState([]);
  const [refundRows, setRefundRows] = useState([]);
  const [decidingRefund, setDecidingRefund] = useState(null);
  const [raisingRefund, setRaisingRefund]   = useState(null); // { entry, event, amount } — self-raised from an overpaid BACS row
  const [exporting, setExporting]           = useState(false);

  const years = useEventYears(sessionToken);

  const load = useCallback(async () => {
    if (!years) return; // still loading - spinner stays up
    setLoading(true);
    setError(null);
    try {
      const seasons = years.filter(y => y >= CURRENT_YEAR);
      const events = (await Promise.all(
        seasons.map(y => api(`/api/secretary/events?year=${y}`, {}, sessionToken))
      )).flat();
      const perEvent = await Promise.all(events.map(async (ev) => {
        const [financialView, refundRequests] = await Promise.all([
          api(`/api/secretary/entries/${ev.CalendarID}/financial-view`, {}, sessionToken),
          api(`/api/secretary/entries/${ev.CalendarID}/refund-requests`, {}, sessionToken),
        ]);
        return { ev, financialView, refundRequests };
      }));

      const bacs = [];
      const refunds = [];
      for (const { ev, financialView, refundRequests } of perEvent) {
        for (const g of computeBacsGroups(financialView.riders || [])) {
          if (bacsNeedsAttention(g)) {
            bacs.push({ ...g, calendar_id: ev.CalendarID, event: ev });
          }
        }
        for (const s of refundRequests) {
          if (s.refund_status === 'needed') {
            refunds.push({ ...s, calendar_id: ev.CalendarID, event: ev });
          }
        }
      }
      bacs.sort((a, b) => (a.event.Date || '').localeCompare(b.event.Date || ''));
      refunds.sort((a, b) => (a.refund_requested_at || '').localeCompare(b.refund_requested_at || ''));
      setBacsRows(bacs);
      setRefundRows(refunds);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, years]);

  useEffect(() => { load(); }, [load]);

  const handleActualSave = async (row, newVal) => {
    try {
      await api(
        `/api/secretary/entries/${row.calendar_id}/bacs-actual-amount`,
        { method: 'PUT', body: JSON.stringify({ bac_reference: row.bac_reference, bacs_actual_amount: newVal }) },
        sessionToken
      );
      setBacsRows(prev => prev
        .map(r => bacsRowKey(r) === bacsRowKey(row) ? { ...r, bacs_actual_amount: newVal } : r)
        .filter(bacsNeedsAttention));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefundActioned = async (row) => {
    try {
      const refundRequests = await api(`/api/secretary/entries/${row.calendar_id}/refund-requests`, {}, sessionToken);
      const stillNeeded = refundRequests
        .filter(s => s.refund_status === 'needed')
        .map(s => ({ ...s, calendar_id: row.calendar_id, event: row.event }));
      setRefundRows(prev => [
        ...prev.filter(r => r.calendar_id !== row.calendar_id),
        ...stillNeeded,
      ].sort((a, b) => (a.refund_requested_at || '').localeCompare(b.refund_requested_at || '')));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportAllCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`/api/secretary/entries/financial-view/export-all?year=${CURRENT_YEAR}`, {
        headers: { 'X-API-Key': API_KEY, Authorization: `Bearer ${sessionToken}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'entries_all_events.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="sec-section">
      <div className="sec-section-header">
        <h2>💰 Financial Dashboard</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            className="btn-add-entry"
            onClick={handleExportAllCsv}
            disabled={exporting}
            style={{ padding: '0.3rem 0.65rem', fontSize: '0.8rem' }}
          >
            {exporting ? 'Preparing…' : `⬇ Download all ${CURRENT_YEAR} entry data`}
          </button>
          <button className="btn-edit" onClick={onBrowseEvents}>
            Browse all events →
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          <p>⚠️ {error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {bacsRows.length === 0 && refundRows.length === 0 ? (
        <EmptyState icon="✅" message="Nothing outstanding — no BACS references or refund requests need attention." />
      ) : (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="sec-section-header">
              <h3 style={{ fontSize: '1rem', color: '#333' }}>Refunds needing action</h3>
              <span className="entry-count-badge">{refundRows.length}</span>
            </div>
            {refundRows.length === 0 ? (
              <EmptyState icon="✅" message="No pending refund requests." />
            ) : (
              <div className="entries-table-wrap">
                <table className="entries-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Rider</th>
                      <th>Requested amount</th>
                      <th className="note-col">Note</th>
                      <th>Requested</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {refundRows.map(row => {
                      const r = row.payload?.rider || {};
                      return (
                        <tr key={row.id}>
                          <td>{eventLabel(row.event)}</td>
                          <td>{[r.firstName, r.surname].filter(Boolean).join(' ') || '—'}</td>
                          <td>{fmtCurrency(row.refund_requested_amount)}</td>
                          <td className="note-col" title={row.refund_note || ''}
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.refund_note || '—'}
                          </td>
                          <td>
                            {row.refund_requested_by || '—'}
                            {row.refund_requested_at ? <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{fmtDateTime(row.refund_requested_at)}</div> : null}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="status-pill status-refund-needed"
                              onClick={() => setDecidingRefund(row)}
                              title="Click to process this refund request"
                            >
                              {refundStatusLabel(row.refund_status)}
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn-edit"
                              onClick={() => onSelectEvent(row.event)}
                            >
                              Open event →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <div className="sec-section-header">
              <h3 style={{ fontSize: '1rem', color: '#333' }}>BACS needing attention</h3>
              <span className="entry-count-badge">{bacsRows.length}</span>
            </div>
            {bacsRows.length === 0 ? (
              <EmptyState icon="✅" message="No outstanding BACS references." />
            ) : (
              <div className="entries-table-wrap">
                <table className="entries-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Reference</th>
                      <th>Entries</th>
                      <th>Fee Total</th>
                      <th title="Amount stated by payer / entered by event secretary">Stated</th>
                      <th title="Amount actually received — enter from bank statement">Actual Received</th>
                      <th title="Difference between actual received and fee total">Adjustment</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bacsRows.map(row => {
                      const feesKnown = row.fees_total > 0;
                      const adjustment = row.bacs_actual_amount != null && feesKnown
                        ? +(row.fees_total - row.bacs_actual_amount).toFixed(2)
                        : null;
                      const overpaid = adjustment != null && adjustment < -0.005;
                      const refundableEntry = overpaid ? row.entries.find(e => e.submission_id) : null;
                      return (
                        <tr key={bacsRowKey(row)} style={adjustment != null && Math.abs(adjustment) > 0.005 ? { background: '#fff3e0' } : {}}>
                          <td>{eventLabel(row.event)}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.88rem' }}>{row.bac_reference}</td>
                          <td style={{ fontSize: '0.82rem' }}>
                            {row.entries.map(e => `#${e.race_number} ${[e.rider_first, e.rider_last].filter(Boolean).join(' ')}`).join(', ')}
                          </td>
                          <td>{feesKnown ? fmtCurrency(row.fees_total) : '—'}</td>
                          <td>{row.bacs_ref_total != null ? fmtCurrency(row.bacs_ref_total) : row.bacs_amount_total > 0 ? fmtCurrency(row.bacs_amount_total) : '—'}</td>
                          <td>
                            <InlineEditCell
                              value={row.bacs_actual_amount}
                              inputType="number"
                              prefix="£"
                              placeholder="0.00"
                              onSave={val => handleActualSave(row, val)}
                            />
                          </td>
                          <td>{adjustment != null ? fmtAdjustment(adjustment, row.fees_total) : '—'}</td>
                          <td style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <button
                              type="button"
                              className="btn-edit"
                              onClick={() => onSelectEvent(row.event)}
                            >
                              Open event →
                            </button>
                            {refundableEntry && (
                              <button
                                type="button"
                                className="btn-edit-lt"
                                title="Raise a refund request for this overpayment"
                                onClick={() => setRaisingRefund({
                                  entry: refundableEntry,
                                  event: row.event,
                                  amount: Math.abs(adjustment),
                                })}
                              >
                                Raise refund…
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {decidingRefund && (
        <RefundActionDialog
          submission={decidingRefund}
          event={{ CalendarID: decidingRefund.calendar_id }}
          sessionToken={sessionToken}
          onClose={() => setDecidingRefund(null)}
          onActioned={() => handleRefundActioned(decidingRefund)}
        />
      )}

      {raisingRefund && (
        <RefundRequestForm
          entry={raisingRefund.entry}
          event={raisingRefund.event}
          initialAmount={String(raisingRefund.amount.toFixed(2))}
          sessionToken={sessionToken}
          onClose={() => setRaisingRefund(null)}
          onSubmitted={() => handleRefundActioned({ calendar_id: raisingRefund.event.CalendarID, event: raisingRefund.event })}
        />
      )}
    </div>
  );
}
